import { Injectable } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { CurrencyType } from '@bciti/types';
import {
    PaymentMethodType,
    ReceiptItemParkingResponse,
    ReceiptItemPrepaidParkingResponse,
    ReceiptItemSubscriptionResponse,
    ReceiptItemParkingPermitResponse,
    ReceiptResponse,
    TransactionType,
} from '@bciti/types';
import { Capacitor } from '@capacitor/core';
import { TranslateService } from '@ngx-translate/core';
import jsPDF from 'jspdf';
import autoTable, { RowInput, UserOptions } from 'jspdf-autotable';
import { MonserratFont } from '../fonts/MontserratFont';

type ReceiptResponseType =
    | ReceiptResponse<ReceiptItemSubscriptionResponse>
    | ReceiptResponse<ReceiptItemParkingResponse>
    | ReceiptResponse<ReceiptItemPrepaidParkingResponse | ReceiptItemParkingPermitResponse>;

@Injectable({
    providedIn: 'root',
})
export class ReceiptPrintService {
    constructor() {}

    translate: TranslateService;

    /**
     *
     * @param receipt a receipt response
     * @param t translation service passed from plugin for localized translations, if passed into the constructor the platform's translateService will be used and will only have access to the platform's translations
     * @returns a string url encoded with the data to generate the receipt AND `output` which is the pdf data in base64 encoding
     */

    public generatePDF(
        receipt: ReceiptResponseType,
        t: TranslateService,
    ): {
        url: SafeResourceUrl | string;
        output: any;
    } {
        this.translate = t;

        const isNativeApp = Capacitor.isNativePlatform();
        const doc = new jsPDF();
        doc.addFileToVFS('Montserrat Medium 500-normal.ttf', MonserratFont);
        doc.addFont('Montserrat Medium 500-normal.ttf', 'Montserrat Medium 500', 'normal');
        if (isNativeApp) {
            doc.setFont('Arial');
        } else {
            doc.setFont('Montserrat Medium 500');
        }

        // Header
        autoTable(doc, {
            body: [
                [
                    {
                        content: this.getHeader(receipt),
                        styles: {
                            halign: 'left',
                            fontSize: 20,
                        },
                    },
                ],
            ],
            theme: 'plain',
            styles: {
                fillColor: '#ffffff',
            },
        });

        // Items
        autoTable(doc, this.getItems(receipt));

        // Transaction details
        autoTable(doc, this.getTransactionDetails(receipt) as UserOptions);

        // Payment method
        if (receipt.paymentMethodType !== PaymentMethodType.PREPAID && receipt.paymentMethodType !== PaymentMethodType.FREE) {
            autoTable(doc, this.getPaymentDetails(receipt) as UserOptions);
        }

        // Failed transaction
        if (!receipt.transactionSuccess) {
            autoTable(doc, this.getTransactionFailedTable() as UserOptions);
        }

        const url = window.URL.createObjectURL(new Blob([doc.output('arraybuffer')], { type: 'application/pdf' }));
        let output;

        if (Capacitor.getPlatform() === 'web' && navigator.userAgent.includes('Chrome')) {
            output = doc.output('datauristring');
        } else {
            output = doc.output('datauri');
        }

        return { url, output };
    }

    /**
     *
     * @param data Receipt response data
     * @returns receipt header string
     */
    private getHeader(data: ReceiptResponseType): string {
        if (data.transactionType === TransactionType.SUBSCRIPTION) {
            return this.translate.instant('pages.user-receipts.detail.header.SUBSCRIPTION');
        }
        return this.translate.instant(`pages.user-receipts.detail.header.PARKING`);
    }

    /**
     *
     * @param data Receipt response data
     * @returns formatted UserOptions configured with a receipt's items details
     */
    private getItems(data: ReceiptResponseType): UserOptions {
        const rows = [];
        switch (data.transactionType) {
            case TransactionType.SUBSCRIPTION:
                (data as ReceiptResponse<ReceiptItemSubscriptionResponse>).items.forEach((item) => {
                    rows.push(this.createBaseTableRow(`${item.description} (${item.code})`, ''));
                });
                rows.push(this.createBaseTableRow(this.translate.instant('pages.user-receipts.detail.city'), data.organisation));
                break;

            case TransactionType.PARKING:
                (data as ReceiptResponse<ReceiptItemParkingResponse>).items.forEach((item) => {
                    if (data.organisation.toUpperCase() === 'UDES') {
                        rows.push(this.createBaseTableRow(this.translate.instant('pages.user-receipts.detail.item.PARKING_UDES'), ''));
                    } else {
                        rows.push(this.createBaseTableRow(this.translate.instant('pages.user-receipts.detail.item.PARKING'), ''));
                    }
                    rows.push(this.createBaseTableRow(this.translate.instant('pages.user-receipts.detail.city'), data.organisation));
                    rows.push(this.createBaseTableRow(this.translate.instant('pages.user-receipts.detail.parking-zone'), item.zone));
                    rows.push(this.createBaseTableRow(this.translate.instant('pages.user-receipts.detail.license-plate'), item.licensePlate));
                    rows.push(this.createBaseTableRow(this.translate.instant('pages.user-receipts.detail.date'), this.getShortDate(item.start))); // dd-mm-yyyy
                    rows.push(this.createBaseTableRow(this.translate.instant('pages.user-receipts.detail.start'), this.getShortTime(item.start))); // hh:mm
                    rows.push(this.createBaseTableRow(this.translate.instant('pages.user-receipts.detail.end'), this.getShortTime(item.end))); // hh:mm
                });
                break;

            case TransactionType.PARKING_PLAN:
                (data as ReceiptResponse<ReceiptItemPrepaidParkingResponse>).items.forEach((item) => {
                    rows.push(this.createBaseTableRow(this.translate.instant('pages.user-receipts.detail.item.PARKING_PLAN'), ''));
                    rows.push(this.createBaseTableRow(this.translate.instant('pages.user-receipts.detail.city'), data.organisation));
                });
                break;

            case TransactionType.PARKING_PERMIT:
                (data as ReceiptResponse<ReceiptItemParkingPermitResponse>).items.forEach((item) => {
                    rows.push(this.createBaseTableRow(this.translate.instant('pages.user-receipts.detail.item.PARKING_PERMIT'), ''));
                    rows.push(this.createBaseTableRow(this.translate.instant('pages.user-receipts.detail.city'), data.organisation));
                });
                break;

            default:
                console.error('receipt-print.service -> unhandled TransactionType');
                break;
        }

        return this.createBaseTable(this.translate.instant('pages.user-receipts.detail.items'), rows);
    }

    /**
     *
     * @param data Receipt response data
     * @returns formatted UserOptions configured to display a receipt's transaction details
     */
    private getTransactionDetails(data: ReceiptResponseType): UserOptions {
        const rows = [];

        let isRefund = data.transactionType === TransactionType.CREDIT;

        rows.push(
            this.createBaseTableRow(
                this.translate.instant('pages.user-receipts.detail.paymentMethodType.label'),
                this.translate.instant(isRefund ? 'pages.user-receipts.detail.paymentMethodType.CREDIT' : `pages.user-receipts.detail.paymentMethodType.${data.paymentMethodType}`),
            ),
        );

        if (data.receiptNumber) {
            rows.push(this.createBaseTableRow(this.translate.instant('pages.user-receipts.detail.receipt-no'), data.receiptNumber));
        }

        rows.push(this.createBaseTableRow(this.translate.instant('pages.user-receipts.detail.date-time'), this.getFullDateTime(data.created_at)));

        rows.push(this.createBaseTableRow(data.items[0].description, this.getCurrencyString(data.items[0].amount)));

        if (data.serviceFees) {
            rows.push(this.createBaseTableRow(this.translate.instant('pages.user-receipts.detail.service-fee'), this.getCurrencyString(data.serviceFees)));
        }

        if (data.subtotal !== undefined && data.subtotal !== null) {
            rows.push(this.createBaseTableRow(this.translate.instant('pages.user-receipts.detail.subtotal'), this.getCurrencyString(data.subtotal)));
        }

        if (data?.taxes?.length > 0) {
            data.taxes.forEach((tax) => {
                rows.push(this.createBaseTableRow(`${tax.name} (${tax.taxNumber})`, this.getCurrencyString(tax.amount)));
            });
        }

        if (data.total !== undefined && data.total !== null) {
            rows.push(this.createBaseTableRow(this.translate.instant('pages.user-receipts.detail.total-price'), this.getCurrencyString(data.total)));
        }

        return this.createBaseTable(this.translate.instant('pages.user-receipts.detail.transaction'), rows);
    }

    /**
     *
     * @param data Receipt response data
     * @returns formatted UserOptions configured to display a receipt's payment details
     */
    private getPaymentDetails(data: ReceiptResponseType): UserOptions {
        const rows = [];

        if (data.paymentMethodType === PaymentMethodType.COUNTER) {
            rows.push(this.createBaseTableRow(this.translate.instant('pages.user-receipts.detail.paymentMethodType.COUNTER'), ''));
            if (data?.paymentDetails?.authorizationNumber) {
                rows.push(this.createBaseTableRow(this.translate.instant('pages.user-receipts.detail.paymentDetails.reference-number'), data.paymentDetails.authorizationNumber));
            }
        } else {
            if (data?.paymentDetails?.cardNumber) {
                rows.push(this.createBaseTableRow(this.translate.instant('pages.user-receipts.detail.paymentDetails.card-number'), data.paymentDetails.cardNumber));
            }

            if (data?.paymentDetails?.cardType) {
                rows.push(this.createBaseTableRow(this.translate.instant('pages.user-receipts.detail.paymentDetails.card-type'), data.paymentDetails.cardType.replace('_', ' ')));
            }

            if (data?.paymentDetails?.transactionNumber) {
                rows.push(this.createBaseTableRow(this.translate.instant('pages.user-receipts.detail.paymentDetails.transaction-number'), data.paymentDetails.transactionNumber));
            }

            if (data?.paymentDetails?.authorizationNumber) {
                rows.push(this.createBaseTableRow(this.translate.instant('pages.user-receipts.detail.paymentDetails.authorization-number'), data.paymentDetails.authorizationNumber));
            }
        }

        if (data?.total !== undefined && data?.total !== null) {
            rows.push(this.createBaseTableRow(this.translate.instant('pages.user-receipts.detail.price'), this.getCurrencyString(data.total)));
        }

        const headerKey = data.transactionType === TransactionType.CREDIT ? 'pages.user-receipts.detail.refund-method' : 'pages.user-receipts.detail.payment-method';
        return this.createBaseTable(this.translate.instant(headerKey), rows);
    }

    getTransactionFailedTable(): UserOptions {
        return this.createBaseTable(this.translate.instant('pages.user-receipts.transaction-failed'), [], 'red');
    }

    //
    // Table builders
    //

    /**
     *
     * @param tableHeader Name of the section
     * @param body RowInput[] ex: [['Label1', 'Value1'], ['Label2', 'Value2']]
     * @returns UserOptions to be used by jspdf-autotable
     */
    private createBaseTable(tableHeader: string, body: RowInput[], headerColor?: string): UserOptions {
        const baseTable = {
            head: [['', '']],
            body: [[]],
            theme: 'plain',
            styles: {
                fillColor: headerColor || '#ffffff',
            },
        };

        return {
            ...baseTable,
            head: [[tableHeader, '']],
            body: body,
        } as UserOptions;
    }

    /**
     *
     * @param label Left-hand side value (ex: Subtotal)
     * @param value Right-hand side value (ex: $10.00)
     * @returns RowInput[] (ex: ['Subtotal', '$10.00'])
     */
    private createBaseTableRow(label: string, value: string | number): RowInput {
        const baseTableItem = {
            content: '',
            styles: {
                halign: 'left',
            },
        };

        const newRowLabel = {
            ...baseTableItem,
            content: label,
        };

        const newRowValue = {
            ...baseTableItem,
            content: value?.toString(),
            styles: {
                halign: 'right',
            },
        };

        const newRow = [newRowLabel, newRowValue];
        return newRow as RowInput;
    }

    //
    // Date and currency formatters
    //
    private getShortDate(date: string): string {
        return new Intl.DateTimeFormat(`${this.translate.currentLang}-CA`, { dateStyle: 'short' } as Intl.DateTimeFormatOptions).format(new Date(date)).replace(/\//g, '-');
    }

    private getShortTime(date: string): string {
        return new Intl.DateTimeFormat(`${this.translate.currentLang}-CA`, { timeStyle: 'short' } as Intl.DateTimeFormatOptions).format(new Date(date)).replace(/\//g, '-');
    }

    private getFullDateTime(date: string): string {
        return new Intl.DateTimeFormat(`${this.translate.currentLang}-CA`, {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
        })
            .format(new Date(date))
            .replace(/\//g, '-');
    }

    private getCurrencyString(amount: string | number, currency: CurrencyType = CurrencyType.CAD): string {
        return new Intl.NumberFormat(this.translate.currentLang, {
            style: 'currency',
            currency: currency,
            currencyDisplay: 'narrowSymbol',
        } as any).format(Number(amount));
    }
}
